/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToWishlistComponent, IWishlistActionErrorResult } from '@msdyn365-commerce/components';
import { format, ItemAvailability, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    getConfigureErrors,
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonResources,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxResources as IBuyboxExtentionResources,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import {
    ArrayExtensions,
    getFullAvailableInventoryNearby,
    GetFullAvailableInventoryNearbyInput,
    IFullOrgUnitAvailability
} from '@msdyn365-commerce-modules/retail-actions';
import { IncrementalQuantity, INodeProps, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { reaction } from 'mobx';
import * as React from 'react';

import { IBuyboxProps as IBuyboxExtentionProps, IBuyboxProps } from '../definition-extensions/buybox.ext.props.autogenerated';
import { renderAddToOrderTemplateButton } from './components/buybox-add-to-order-template';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { IBuyboxData } from './buybox.data';
import classnames from 'classnames';
import * as Msdyn365 from '@msdyn365-commerce/core';

/**
 * BuyBoxConstants enum.
 */
export enum BuyBoxConstants {
    zero = 0
}

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

/**
 * RenderQuantity.
 * @param quantityComponent - QuantityComponent.
 * @param callbacks - Callbacks.
 * @param props - Props.
 * @param state - State.
 * @param extentionResources - ExtentionResources.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param max - Max.
 * @param telemetryContent - TelemetryContent.
 * @param unitOfMeasure - Unit Of Measure.
 * @returns -- Returns.
 */
const renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    min: number | undefined,
    max: number | undefined,
    applyDefaultOrderSettings?: boolean,
    telemetryContent?: ITelemetryContent,
    unitOfMeasure?: React.ReactNode
): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                min={min}
                max={max}
                applyDefaultOrderSettings={applyDefaultOrderSettings}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {unitOfMeasure}
            {quantityLimitsMessages}
        </Node>
    );
};

/**
 * Render key in price function.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */
const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

/**
 * RenderFindInStore.
 * @param findInStore - FindInStore.
 * @returns -- Returns.
 */
const renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * RenderAddToOrderTemplate.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToOrderTemplate = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToOrderTemplate || !product) {
        return null;
    }

    const orderTemplateButton = renderAddToOrderTemplateButton(props, state, callbacks, product);

    return (
        <Node {...props.addToOrderTemplate.ContainerProps}>
            {props.addToOrderTemplate.errorBlock}
            {orderTemplateButton}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (callbacks: IBuyboxCallbacks, resources: IBuyboxCommonResources, product: SimpleProduct | undefined) => (
    result: IWishlistActionErrorResult
) => {
    callbacks.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    product: SimpleProduct
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    const isShowWishlitButtonText = true;

    const addToWishlistButtonText = 'Add to Wishlist';
    const removeFromWishlistButtonText = 'Remove from Wishlist';
    const addToWishlistMessage = 'Added to your Wishlist';
    const removedFromWishlistMessage = 'Item removed from Wishlist';
    const addItemToWishlistError = 'Unable to add item to Wishlist';
    const removeItemFromWishlistError = 'Unable to remove item from Wishlist';
    const nameOfWishlist = 'My Wishlist';
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={addToWishlistButtonText}
            removeFromWishlistButtonText={removeFromWishlistButtonText}
            addToWishlistMessage={addToWishlistMessage}
            removedFromWishlistMessage={removedFromWishlistMessage}
            addItemToWishlistError={addItemToWishlistError}
            removeItemFromWishlistError={removeItemFromWishlistError}
            nameOfWishlist={nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            ariaRole='button'
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToWishlist || !product) {
        return null;
    }

    if (!props.config.enableWishlist) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product);
    return (
        <Node {...props.addToWishlist.ContainerProps}>
            {props.addToWishlist.errorBlock}
            {wishlistButton}
        </Node>
    );
};

/**
 * RRenderShopSimilarItem.
 * @param shopSimilarItem - ShopSimilarItem.
 * @returns -- Returns.
 */
const renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

/**
 * RenderSocialShare.
 * @param socialShare - Social share react node array.
 * @returns -- Returns.
 */
const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

/**
 * BuyboxView.
 * @param props - Props.
 * @returns -- Returns.
 */
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const {
        ModuleProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        MediaGalleryContainerProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        productComparisonButton,
        configure,
        findInStore,
        quantity,
        price,
        rating,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        min,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        max,
        applyDefaultOrderSettings,
        unitOfMeasure,
        callbacks,
        state,
        resources,
        quantityLimitsMessages,
        telemetryContent,
        app: { config: appConfig },
        catalogs
    } = props;
    let skuText: string = props.resources.skuText;
    skuText += props.data.product.result?.ItemId;
    const preferredStoreInfo: IStoreInfo | undefined = props.data.storeSelectorStateManager.result?.preferredStore;
    const preferredStoreId = preferredStoreInfo?.StoreId ? preferredStoreInfo.StoreId : '';
    const preferredStoreName = preferredStoreInfo?.StoreName ? preferredStoreInfo.StoreName : '';

    const [availability, setAvailability] = React.useState<string>('');
    const [isStoreChange, setIsStoreChange] = React.useState<boolean>(false);

    /**
     * GetStockInventoryLabel.
     * @param availabilityWithHours - Stock availability with hours.
     * @returns -- Returns.
     */
    const getStockInventoryLabel = (availabilityWithHours: IFullOrgUnitAvailability | undefined): string | undefined => {
        if (availabilityWithHours && ArrayExtensions.hasElements(availabilityWithHours.ProductInventoryInformation)) {
            for (const productInventoryInformation of availabilityWithHours.ProductInventoryInformation) {
                if (
                    availabilityWithHours.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId ===
                    productInventoryInformation.InventLocationId
                ) {
                    return productInventoryInformation.StockLevelLabel;
                }
            }
        }

        return undefined;
    };

    const getInventory = React.useCallback(async () => {
        const input = new GetFullAvailableInventoryNearbyInput(
            props.data.product.result?.RecordId,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            true
        );

        /**
         * IsProductInStock.
         * @param itemAvailabilities - Item availability array.
         * @returns -- Returns.
         */
        const isProductInStock = (itemAvailabilities: ItemAvailability[] | undefined): boolean => {
            if (!appConfig.enableStockCheck) {
                return true;
            }

            if (ArrayExtensions.hasElements(itemAvailabilities)) {
                return itemAvailabilities[BuyBoxConstants.zero].AvailableQuantity! > BuyBoxConstants.zero;
            }

            return false;
        };

        await getFullAvailableInventoryNearby(input, props.context.actionContext)
            .catch((error: Error) => {
                props.telemetry.error(error.message);
            })
            .then(response => {
                if (response && ArrayExtensions.hasElements(response)) {
                    const isPreferredStore = (value: IFullOrgUnitAvailability) =>
                        value.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === preferredStoreId;
                    const filteredFullOrgUnitAvailability = response.filter(isPreferredStore);
                    const preferredStoreAvailability = ArrayExtensions.hasElements(filteredFullOrgUnitAvailability)
                        ? filteredFullOrgUnitAvailability[BuyBoxConstants.zero]
                        : undefined;
                    const stockStatusLabel: string | undefined = getStockInventoryLabel(preferredStoreAvailability);
                    const isInStock = isProductInStock(preferredStoreAvailability?.OrgUnitAvailability?.ItemAvailabilities);
                    const resourceLabel = isInStock ? props.resources.inStockText : props.resources.outOfStockText;
                    const stockText: string | undefined = stockStatusLabel ? stockStatusLabel : resourceLabel;
                    const preferredStoreAvailabilityText = `${format(
                        props.resources.availabilityAtPreferredStoreText,
                        preferredStoreName
                    )} ${stockText}`;
                    setAvailability(preferredStoreAvailabilityText);
                }
            });

        setIsStoreChange(false);
    }, [
        props.data.product.result?.RecordId,
        appConfig.enableStockCheck,
        preferredStoreId,
        preferredStoreName,
        props.context.actionContext,
        props.resources.availabilityAtPreferredStoreText,
        props.resources.inStockText,
        props.resources.outOfStockText,
        props.telemetry
    ]);

    React.useEffect(() => {
        if (props.config.displayStockAvailability) {
            getInventory().catch((error: Error) => {
                props.telemetry.error(error.message);
            });
        }
    }, [getInventory, isStoreChange, props.telemetry, props.config.displayStockAvailability]);

    reaction(
        () => props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
        () => {
            setIsStoreChange(true);
        }
    );

    /**
     * RenderAvailabiltyAtPreferredStore.
     * @returns -- Returns.
     */
    const renderAvailabilityAtPreferredStore = (): JSX.Element | undefined => {
        const hasProductDimensions = ArrayExtensions.hasElements(props.data.product.result?.Dimensions);
        if (hasProductDimensions && props.data.product.result?.MasterProductId === undefined) {
            return undefined;
        }
        const containerProps: INodeProps = {
            className: 'ms-buybox__preferred-store-availability',
            tag: 'span'
        };

        return <Node {...containerProps}>{availability}</Node>;
    };

    const VAT_RATE = 1.23; 

    const processPrice = (price: React.ReactNode): React.ReactNode => {
        if (React.isValidElement(price) && price.props?.data?.price) {
            const { price: priceData } = price.props.data;

            const base = (typeof priceData.ActivePrice === 'number')
                ? priceData.ActivePrice
                : (typeof priceData.AdjustedPrice === 'number'
                    ? priceData.AdjustedPrice - (priceData.DiscountAmount ?? 0)
                    : undefined);
            if (typeof base === 'number') {
                const final = (base * VAT_RATE).toFixed(2);
                return <span className="msc-buybox__price">€{final}</span>;
            }
        }
        return price;
    };

    const isB2B = props.context?.request?.apiSettings?.channelCustomerType === 'B2B';
    const vatLabel = isB2B ? ' (ex VAT)' : ' (inc VAT)';

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {catalogs && catalogs[0].Name}
                {getBuyboxProductName(props)}
                <Node className='msc-buybox__ratings-section'>
                    <div className='msc-buybox__ratings-section-brand-text'>{getBrandName(props)}</div>
                    <div className='msc-buybox__ratings-section-sku-text'>{skuText}</div>
                    {rating}
                </Node>
                <Node className='msc-buybox__price-section'>
                    <div className='msc-buybox__price-section-text'>{props.resources.priceText}</div>
                    <span className='msc-buybox__price'>{isB2B ? price : processPrice(price)}</span>
                    {vatLabel}
                </Node>
                {configure && renderConfigure(configure)}
                {keyInPrice && renderKeyInPrice(keyInPrice)}
                <Node className='msc-buybox__bulk-purchase-section'>
                    <div className='msc-buybox__bulk-purchase-button-text'>
                        {quantity &&
                            renderQuantity(
                                quantity,
                                callbacks,
                                props,
                                state,
                                resources,
                                quantityLimitsMessages,
                                min,
                                max,
                                applyDefaultOrderSettings,
                                telemetryContent,
                                unitOfMeasure
                            )}
                        {props.bulkPurchaseLink}
                    </div>
                </Node>
                {props.config.displayStockAvailability && preferredStoreId && renderAvailabilityAtPreferredStore()}
                {inventoryLabel}
                {renderAddToCart(addToCart)}
                {findInStore && renderFindInStore(findInStore)}
                {addToWishlist && renderAddToWishlist(props, state, callbacks)}
                {productComparisonButton}
                {addToOrderTemplate && renderAddToOrderTemplate(props, state, callbacks)}
                {findInStore?.productPickupOptionList ? (
                    <div className='msc-buybox__pickup-options'>{findInStore.productPickupOptionList}</div>
                ) : null}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                <div className='msc-buybox__description-text'>{props.resources.shopText}</div>
                <Node className='msc-buybox__shop-description'>
                    {shopSimilarLook && renderShopSimilarItem(shopSimilarLook)}
                    {shopSimilarDescription && renderShopSimilarItem(shopSimilarDescription)}
                </Node>
                <Node className='msc-buybox__volume-based-message'>{getBuyboxVolumeBasedMessage(props)}</Node>
                <Node className='msc-buybox__description-section'>
                    <div className='msc-buybox__description-section-text'>{props.resources.descriptionText}</div>
                    {getBuyboxProductDescription(props)}
                </Node>
                {ArrayExtensions.hasElements(props.slots.productSpecification) ? (
                    <Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
                ) : null}
            </Node>
        </Module>
    );
};

export function getBuyboxProductName(
    props: React.PropsWithChildren<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>>
): React.ReactNode | undefined {
    const {
        data: {
            product: { result: product },
            productAttributes: { result: productAttributes }
        },
        config,
        title,
        app
    } = props;
    const hsoProductNameAttributeName =
        (app.config.hsoRexelProductNameAttribute && app.config.hsoRexelProductNameAttribute.toUpperCase()) || 'ProductName';
    const hsoProductName = hsoProductNameAttributeName && getAttributeText(hsoProductNameAttributeName, productAttributes);
    if (app.config.hsoRexelProductName && hsoProductName != undefined && hsoProductName != '') {
        return (
            product && (
                <div className={classnames('ms-buybox__product-title', config.className)}>
                    <h1>{hsoProductName}</h1>
                </div>
            )
        );
    }
    return title;
}

export function getBrandName(
    props: React.PropsWithChildren<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>>
): React.ReactElement | undefined {
    const environmentName = props.context.request.apiSettings.baseUrl;

    const {
        data: {
            productAttributes: { result: productAttributes }
        },
        app
    } = props;
    const hsoBrandNameAttribute = (app.config.hsoRexelBrandName && app.config.hsoRexelBrandNameAttribute.toUpperCase()) || 'BRAND';
    const hsoBrand = hsoBrandNameAttribute && getAttributeText(hsoBrandNameAttribute, productAttributes);
    if (environmentName === 'https://scufsoqr9kv88782977-rs.su.retail.dynamics.com/') {
        if (app.config.hsoRexelBrandName && hsoBrand != undefined && hsoBrand != '') {
            const redirectURL = '/root/energy-management/5637302828.c?refiners=[[5,"<BRAND>",5637147579,1,"<BRAND>","",0]]';
            const rebrandedURL = redirectURL.split('<BRAND>').join(hsoBrand);
            const navigationURL = props.context.request.sitePath + rebrandedURL;
            return (
                <span className='msc-buybox__brand-name'>
                    by <a href={navigationURL}>{hsoBrand}</a>
                </span>
            );
        }
    }
    if (environmentName === 'https://scuxs04mryq61424839-rs.su.retail.dynamics.com/') {
        if (app.config.hsoRexelBrandName && hsoBrand != undefined && hsoBrand != '') {
            const redirectURL = '/new-category/energy-management/5637295326.c?refiners=[[5,"<BRAND>",5637150576,1,"<BRAND>","",0]]';
            const rebrandedURL = redirectURL.split('<BRAND>').join(hsoBrand);
            const navigationURL = props.context.request.sitePath + rebrandedURL;
            return (
                <span className='msc-buybox__brand-name'>
                    by <a href={navigationURL}>{hsoBrand}</a>
                </span>
            );
        }
    }
    if (environmentName === 'https://scuke6781jy72352123-rs.su.retail.dynamics.com/') {
        if (app.config.hsoRexelBrandName && hsoBrand != undefined && hsoBrand != '') {
            const redirectURL = '/root/energy-solutions/5637308828.c?refiners=[[5,"<BRAND>",5637147579,1,"<BRAND>","",0]]';
            const rebrandedURL = redirectURL.split('<BRAND>').join(hsoBrand);
            const navigationURL = props.context.request.sitePath + rebrandedURL;
            return (
                <span className='msc-buybox__brand-name'>
                    by <a href={navigationURL}>{hsoBrand}</a>
                </span>
            );
        }
    }

    return undefined;
}

export function getBuyboxProductDescription(
    props: React.PropsWithChildren<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>>
): React.ReactElement | undefined {
    const {
        data: {
            product: { result: product },
            productAttributes: { result: productAttributes }
        },
        config,
        app
    } = props;
    const description = product && product.Description ? product.Description.replace(/<[^>]+>/g, '') : '';
    const hsoProductDescriptionAttributeName =
        (app.config.hsoRexelProductDescriptionAttribute && app.config.hsoRexelProductDescriptionAttribute.toUpperCase()) ||
        'ProductDescription';
    const hsoProductDescription =
        hsoProductDescriptionAttributeName && getAttributeText(hsoProductDescriptionAttributeName, productAttributes);
    if (app.config.hsoRexelProductDescription && hsoProductDescription != undefined && hsoProductDescription != '') {
        return product && <Msdyn365.RichTextComponent text={hsoProductDescription} className='ms-buybox__product-description' />;
    }
    return (
        product && (
            <div className={classnames('ms-buybox__product-description', config.className)}>
                <p>{description}</p>
            </div>
        )
    );
}

export function getBuyboxVolumeBasedMessage(
    props: React.PropsWithChildren<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>>
): React.ReactElement | undefined {
    const {
        data: {
            product: { result: product },
            productAttributes: { result: productAttributes }
        },
        app
    } = props;
    const hsoVolumeBasedMessageAttribute =
        (app.config.hsoRexelVolumeBasedMessageAttribute && app.config.hsoRexelVolumeBasedMessageAttribute.toUpperCase()) ||
        'VolumeBasedMessage';
    const hsoVolumeBasedMessage = hsoVolumeBasedMessageAttribute && getAttributeText(hsoVolumeBasedMessageAttribute, productAttributes);
    if (app.config.hsoRexelVolumeBasedMessage && hsoVolumeBasedMessage != undefined && hsoVolumeBasedMessage != '') {
        return product && <Msdyn365.RichTextComponent text={hsoVolumeBasedMessage} className='ms-buybox__product-volume-based' />;
    }
    return undefined;
}

function getAttributeText(attributeName: string, productAttributes: AttributeValue[] | undefined): string | undefined {
    if (productAttributes && productAttributes.length) {
        const filterAttributes = productAttributes.filter(attribute => attribute.Name && attribute.Name.toUpperCase() === attributeName);
        return filterAttributes && filterAttributes.length > 0 && filterAttributes[0].TextValue ? filterAttributes[0].TextValue : undefined;
    }
    return;
}

export default BuyboxView;
